// ============================================================
// Pay Rocky - OrdersContext
// Server-backed orders, proof uploads, and admin order actions.
// ============================================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { apiRequest } from '../utils/api';
import { getIdempotencyKey, releaseIdempotencyKey, shouldRetainIdempotencyKey } from '../utils/idempotency';
import { readCachedValue, writeCachedValue } from '../utils/storage';

const OrdersContext = createContext(null);
const ORDERS_CACHE_TTL_MS = 60 * 1000;
const ORDERS_REQUEST_TIMEOUT_MS = 10000;
const ORDERS_PAGE_SIZE = 50;

function normalizeOrder(order) {
  const timeline = Array.isArray(order?.timeline)
    ? order.timeline.map((item) => ({
        ...item,
        id: String(item?.id || ''),
        label: String(item?.label || ''),
        description: String(item?.description || ''),
        at: item?.at || '',
        done: Boolean(item?.done),
      }))
    : [];
  const internalNotes = Array.isArray(order?.internal_notes)
    ? order.internal_notes.map((item) => ({
        id: String(item?.id || ''),
        author_id: String(item?.author_id || ''),
        author_name: String(item?.author_name || ''),
        body: String(item?.body || ''),
        created_at: item?.created_at || '',
      }))
    : [];
  const operationsChecklist = {
    payment_confirmed: Boolean(order?.operations_checklist?.payment_confirmed),
    proof_reviewed: Boolean(order?.operations_checklist?.proof_reviewed),
    wallet_verified: Boolean(order?.operations_checklist?.wallet_verified),
    payout_approved: Boolean(order?.operations_checklist?.payout_approved),
    payout_sent: Boolean(order?.operations_checklist?.payout_sent),
  };

  return {
    ...order,
    id: order.id || order._id,
    user_id: order.user_id || order.user?.id,
    rate_locked_until: order.rate_locked_until || '',
    workflow_stage: order.workflow_stage || '',
    operations_checklist: operationsChecklist,
    is_flagged: Boolean(order?.is_flagged),
    flag_reason: String(order?.flag_reason || ''),
    dispute_status: String(order?.dispute_status || 'none'),
    internal_notes: internalNotes,
    created_at: order.created_at || '',
    updated_at: order.updated_at || '',
    timeline,
  };
}

export function OrdersProvider({ children }) {
  const { currentUser, token } = useAuth();
  const cacheKey = currentUser ? `payrocky_orders_${currentUser.role}_${currentUser.id}` : '';
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ordersError, setOrdersError] = useState('');
  const [ordersMeta, setOrdersMeta] = useState({
    page: 0,
    total: 0,
    limit: ORDERS_PAGE_SIZE,
    hasMore: false,
  });

  const persistOrders = useCallback((nextOrders) => {
    setOrders(nextOrders);
    if (cacheKey) writeCachedValue(cacheKey, nextOrders);
  }, [cacheKey]);

  const upsertOrder = useCallback((nextOrder) => {
    const normalizedOrder = normalizeOrder(nextOrder);
    setOrders((currentOrders) => {
      const existingIndex = currentOrders.findIndex(
        (order) => order.order_id === normalizedOrder.order_id || order.id === normalizedOrder.id,
      );
      const nextOrders = existingIndex === -1
        ? [normalizedOrder, ...currentOrders]
        : currentOrders.map((order, index) => (index === existingIndex ? normalizedOrder : order));
      if (cacheKey) writeCachedValue(cacheKey, nextOrders);
      return nextOrders;
    });
    return normalizedOrder;
  }, [cacheKey]);

  const refreshOrders = useCallback(async (options = {}) => {
    const force = Boolean(options.force);
    const page = Math.max(1, Number(options.page || 1));
    const limit = Math.min(Math.max(1, Number(options.limit || ORDERS_PAGE_SIZE)), 100);
    const append = Boolean(options.append) && page > 1;

    if (!token || !currentUser) {
      setOrders([]);
      setOrdersError('');
      setOrdersMeta({ page: 0, total: 0, limit: ORDERS_PAGE_SIZE, hasMore: false });
      return [];
    }

    let cachedResult = null;
    if (!force && !append && cacheKey) {
      const cachedOrders = readCachedValue(cacheKey, ORDERS_CACHE_TTL_MS, null);
      if (cachedOrders) {
        const normalizedCachedOrders = Array.isArray(cachedOrders) ? cachedOrders.map(normalizeOrder) : [];
        setOrders(normalizedCachedOrders);
        cachedResult = normalizedCachedOrders;
      }
    }

    if (!cachedResult) {
      setLoading(true);
    }
    setOrdersError('');
    try {
      if (currentUser.role === 'admin') {
        const res = await apiRequest(`/admin/orders?limit=${limit}&page=${page}`, { token, timeoutMs: ORDERS_REQUEST_TIMEOUT_MS });
        const nextOrdersRaw = Array.isArray(res.orders) ? res.orders : [];
        const nextOrders = nextOrdersRaw.map(normalizeOrder);
        const total = Math.max(0, Number(res.total || nextOrders.length));
        let mergedOrders = nextOrders;
        if (append) {
          setOrders((prev) => {
            const seen = new Set();
            mergedOrders = [...prev, ...nextOrders].filter((order) => {
              const key = String(order.order_id || order.id || '');
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
            if (cacheKey) writeCachedValue(cacheKey, mergedOrders);
            return mergedOrders;
          });
        } else {
          persistOrders(nextOrders);
        }
        setOrdersMeta({
          page,
          total,
          limit,
          hasMore: page * limit < total,
        });
        return mergedOrders;
      }

      const res = await apiRequest(`/orders?limit=${limit}&page=${page}`, { token, timeoutMs: ORDERS_REQUEST_TIMEOUT_MS });
      const nextOrdersRaw = Array.isArray(res.orders) ? res.orders : [];
      const nextOrders = nextOrdersRaw.map(normalizeOrder);
      const total = Math.max(0, Number(res.total || nextOrders.length));
      if (!append) {
        persistOrders(nextOrders);
      } else {
        let mergedOrders = nextOrders;
        setOrders((prev) => {
          const seen = new Set();
          mergedOrders = [...prev, ...nextOrders].filter((order) => {
            const key = String(order.order_id || order.id || '');
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
          if (cacheKey) writeCachedValue(cacheKey, mergedOrders);
          return mergedOrders;
        });
      }
      setOrdersMeta({
        page,
        total,
        limit,
        hasMore: page * limit < total,
      });
      return nextOrders;
    } catch (error) {
      setOrdersError(error.message || 'Failed to load orders.');
      if (cachedResult) {
        setOrdersMeta((prev) => ({
          ...prev,
          page: cachedResult.length > 0 ? Math.max(prev.page, Math.ceil(cachedResult.length / limit)) : prev.page,
          limit,
          total: Math.max(prev.total, cachedResult.length),
          hasMore: Math.max(prev.total, cachedResult.length) > cachedResult.length,
        }));
        return cachedResult;
      }
      throw error;
    } finally {
      setLoading(false);
    }
  }, [token, currentUser, cacheKey, persistOrders]);

  useEffect(() => {
    refreshOrders({ page: 1, force: false }).catch(() => {
      setOrders([]);
      setOrdersMeta({ page: 0, total: 0, limit: ORDERS_PAGE_SIZE, hasMore: false });
      setLoading(false);
      });
  }, [token, currentUser?.id, currentUser?.role]);

  const loadMoreOrders = useCallback(async () => {
    if (!ordersMeta.hasMore || loading) return orders;
    return refreshOrders({
      page: ordersMeta.page + 1,
      limit: ordersMeta.limit || ORDERS_PAGE_SIZE,
      append: true,
      force: true,
    });
  }, [ordersMeta, loading, orders, refreshOrders]);

  const getUserOrders = (userId) => orders.filter((order) => String(order.user_id) === String(userId));
  const getOrder = (orderId) => orders.find((order) => order.order_id === orderId || order.id === orderId);

  const fetchOrderById = useCallback(async (orderId) => {
    if (!token || !currentUser || !orderId) return null;

    const endpoint = currentUser.role === 'admin'
      ? `/admin/order/${encodeURIComponent(orderId)}`
      : `/user/order/${encodeURIComponent(orderId)}`;

    const res = await apiRequest(endpoint, {
      token,
      timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
    });

    if (!res?.order) return null;
    return upsertOrder(res.order);
  }, [token, currentUser, upsertOrder]);

  const placeBuyOrder = async (payload) => {
    const idempotencyPayload = {
      cryptoType: payload.cryptoType,
      cryptoAmount: payload.cryptoAmount,
      usdValue: payload.usdValue,
      feeUsd: payload.feeUsd,
      totalUsdValue: payload.totalUsdValue,
      nairaValue: payload.nairaValue,
      walletAddress: payload.walletAddress,
      network: payload.network,
      payerName: payload.payerName,
      walletAddressExpires: payload.walletAddressExpires,
      walletExpiryContactPhone: payload.walletExpiryContactPhone,
      walletExpiryMinutes: payload.walletExpiryMinutes,
      quoteLockToken: payload.quoteLockToken || '',
      paymentNameConfirmed: Boolean(payload.paymentNameConfirmed),
    };
    const idempotencyKey = getIdempotencyKey('orders:buy', idempotencyPayload);
    try {
      const res = await apiRequest('/orders/buy', {
        method: 'POST',
        token,
        timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
        idempotencyKey,
        body: {
          ...idempotencyPayload,
        },
      });

      releaseIdempotencyKey('orders:buy', idempotencyPayload);
      const order = upsertOrder(res.order);
      return { success: true, order, bankDetails: res.bankDetails };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('orders:buy', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const placeSellOrder = async (payload) => {
    const idempotencyPayload = {
      cryptoType: payload.cryptoType,
      cryptoAmount: payload.cryptoAmount,
      usdValue: payload.usdValue,
      nairaValue: payload.nairaValue,
      network: payload.network,
      bankName: payload.bankName,
      accountName: payload.accountName,
      accountNumber: payload.accountNumber,
      quoteLockToken: payload.quoteLockToken || '',
    };
    const idempotencyKey = getIdempotencyKey('orders:sell', idempotencyPayload);
    try {
      const res = await apiRequest('/orders/sell', {
        method: 'POST',
        token,
        timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
        idempotencyKey,
        body: {
          ...idempotencyPayload,
        },
      });

      releaseIdempotencyKey('orders:sell', idempotencyPayload);
      const order = {
        ...upsertOrder(res.order),
        depositWallet: res.depositWallet,
      };
      return { success: true, order, depositWallet: res.depositWallet };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('orders:sell', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const uploadProof = async (orderId, file) => {
    const idempotencyPayload = {
      orderId,
      fileName: file?.name || '',
      fileSize: Number(file?.size || 0),
      fileType: file?.type || '',
      fileModifiedAt: Number(file?.lastModified || 0),
    };
    const idempotencyKey = getIdempotencyKey('orders:upload-proof', idempotencyPayload);
    try {
      const formData = new FormData();
      formData.append('orderId', orderId);
      formData.append('proof', file);

      const res = await apiRequest('/orders/upload-proof', {
        method: 'POST',
        token,
        body: formData,
        timeoutMs: 12000,
        idempotencyKey,
      });

      releaseIdempotencyKey('orders:upload-proof', idempotencyPayload);
      const updated = upsertOrder(res.order);
      return { success: true, order: updated };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('orders:upload-proof', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const cancelOrder = async (orderId) => {
    const idempotencyPayload = { orderId };
    const idempotencyKey = getIdempotencyKey('orders:cancel', idempotencyPayload);
    try {
      const res = await apiRequest('/orders/cancel', {
        method: 'POST',
        token,
        idempotencyKey,
        body: { orderId },
        timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
      });
      releaseIdempotencyKey('orders:cancel', idempotencyPayload);
      const updated = upsertOrder(res.order);
      return { success: true, order: updated, message: res.message };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('orders:cancel', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const adminApprove = async (orderId) => {
    const idempotencyPayload = { orderId };
    const idempotencyKey = getIdempotencyKey('admin:approve-order', idempotencyPayload);
    try {
      const res = await apiRequest('/admin/approve-order', {
        method: 'POST',
        token,
        idempotencyKey,
        body: { orderId },
        timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
      });
      releaseIdempotencyKey('admin:approve-order', idempotencyPayload);
      const updated = upsertOrder(res.order);
      return { success: true, order: updated };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:approve-order', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const adminReject = async (orderId) => {
    const idempotencyPayload = { orderId };
    const idempotencyKey = getIdempotencyKey('admin:reject-order', idempotencyPayload);
    try {
      const res = await apiRequest('/admin/reject-order', {
        method: 'POST',
        token,
        idempotencyKey,
        body: { orderId },
        timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
      });
      releaseIdempotencyKey('admin:reject-order', idempotencyPayload);
      const updated = upsertOrder(res.order);
      return { success: true, order: updated };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:reject-order', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  };

  const adminComplete = adminApprove;

  const requestQuoteLock = useCallback(async ({ type, cryptoType, network }) => {
    const res = await apiRequest('/orders/quote-lock', {
      method: 'POST',
      token,
      timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
      body: {
        type,
        cryptoType,
        network,
      },
    });
    return res.quoteLock || null;
  }, [token]);

  const adminUpdateOrderWorkflow = useCallback(async (orderId, payload) => {
    const idempotencyPayload = {
      orderId,
      workflow_stage: payload.workflow_stage || '',
      dispute_status: payload.dispute_status || '',
      is_flagged: Boolean(payload.is_flagged),
      flag_reason: payload.flag_reason || '',
      note: payload.note || '',
      operations_checklist: payload.operations_checklist || {},
    };
    const idempotencyKey = getIdempotencyKey('admin:update-order-workflow', idempotencyPayload);
    try {
      const res = await apiRequest('/admin/update-order-workflow', {
        method: 'POST',
        token,
        timeoutMs: ORDERS_REQUEST_TIMEOUT_MS,
        idempotencyKey,
        body: {
          orderId,
          ...payload,
        },
      });
      releaseIdempotencyKey('admin:update-order-workflow', idempotencyPayload);
      const updated = upsertOrder(res.order);
      return { success: true, order: updated };
    } catch (error) {
      if (!shouldRetainIdempotencyKey(error)) {
        releaseIdempotencyKey('admin:update-order-workflow', idempotencyPayload);
      }
      return { success: false, error: error.message };
    }
  }, [token, upsertOrder]);

  const getStats = () => ({
    total: orders.length,
    pending: orders.filter((order) => order.status === 'PENDING').length,
    processing: orders.filter((order) => order.status === 'PROCESSING').length,
    completed: orders.filter((order) => order.status === 'COMPLETED').length,
    cancelled: orders.filter((order) => order.status === 'CANCELLED').length,
    dailyVolume: orders
      .filter((order) => order.status === 'COMPLETED')
      .filter((order) => new Date(order.created_at).toDateString() === new Date().toDateString())
      .reduce((sum, order) => sum + Number(order.naira_value || 0), 0),
    completedVolume: orders
      .filter((order) => order.status === 'COMPLETED')
      .reduce((sum, order) => sum + Number(order.naira_value || 0), 0),
  });

  const value = useMemo(() => ({
    orders,
    loading,
    ordersError,
    ordersMeta,
    refreshOrders,
    loadMoreOrders,
    requestQuoteLock,
    getUserOrders,
    getOrder,
    fetchOrderById,
    placeBuyOrder,
    placeSellOrder,
    uploadProof,
    cancelOrder,
    adminApprove,
    adminReject,
    adminComplete,
    getStats,
    adminUpdateOrderWorkflow,
  }), [
    adminApprove,
    adminComplete,
    adminReject,
    adminUpdateOrderWorkflow,
    cancelOrder,
    fetchOrderById,
    loadMoreOrders,
    loading,
    orders,
    ordersError,
    ordersMeta,
    placeBuyOrder,
    placeSellOrder,
    refreshOrders,
    requestQuoteLock,
    uploadProof,
  ]);

  return <OrdersContext.Provider value={value}>{children}</OrdersContext.Provider>;
}

export function useOrders() {
  const ctx = useContext(OrdersContext);
  if (!ctx) throw new Error('useOrders must be used inside <OrdersProvider>');
  return ctx;
}
